import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Gift, Ticket, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  isSignedIn,
  loginUser,
  redeemPartnerCode,
  registerUser,
  whoami,
} from '@/lib/api';

/**
 * "Got a code?" modal — redeems a partner-code for one free entry.
 *
 * Flow (as spec'd by the partnership product brief):
 *   1. Entry: opened from the mobile hamburger menu.
 *   2. Sign-up first: if the user is NOT signed in, we show a lightweight
 *      register/login form BEFORE the code input. This captures the account
 *      even if the user then abandons the code step — we never lose the lead.
 *      If the user IS already signed in, we skip straight to the code step.
 *   3. Code entry: single input + submit → POST /partner-codes/redeem.
 *   4. Success: warm "You're in!" confirmation with an optional, low-key
 *      "Want to buy more entries?" upsell that links to the competition page
 *      (same odd/even pricing flow users already know).
 *
 * The backend enforces everything (code must be active, user signed in,
 * one redemption per user per code, cap-safe, decrements the ticket counter);
 * we just translate its friendly error strings to toast / inline UI here.
 */

const STEP_AUTH = 'auth';
const STEP_CODE = 'code';
const STEP_SUCCESS = 'success';

const OVERLAY_INITIAL = { opacity: 0 };
const OVERLAY_ANIMATE = { opacity: 1 };
const OVERLAY_EXIT = { opacity: 0 };
const PANEL_INITIAL = { opacity: 0, y: 20, scale: 0.98 };
const PANEL_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const PANEL_EXIT = { opacity: 0, y: 12, scale: 0.98 };

const EMPTY_REGISTER = {
  email: '',
  password: '',
  full_name: '',
  date_of_birth: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  marketing_opt_in: false,
  spend_limit_gbp: 50,
  no_spend_limit_acknowledged: false,
};

const EMPTY_LOGIN = { email: '', password: '' };

const errMsg = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

export const PartnerCodeModal = ({ open, onClose }) => {
  const alreadySignedIn = useMemo(() => isSignedIn(), [open]); // re-evaluate when opened
  const [step, setStep] = useState(alreadySignedIn ? STEP_CODE : STEP_AUTH);
  const [authMode, setAuthMode] = useState('register'); // 'register' | 'login'
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER);
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [result, setResult] = useState(null); // { competition, ticket_number, partnership_label }

  // Reset state every time the modal is opened so a second use is fresh.
  useEffect(() => {
    if (open) {
      setStep(isSignedIn() ? STEP_CODE : STEP_AUTH);
      setAuthMode('register');
      setRegisterForm(EMPTY_REGISTER);
      setLoginForm(EMPTY_LOGIN);
      setCode('');
      setCodeError('');
      setResult(null);
      setBusy(false);
    }
  }, [open]);

  // Body scroll lock + ESC-to-close while modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const persistAuth = (token, user) => {
    try {
      localStorage.setItem('pd_user_token', token);
      if (user) localStorage.setItem('pd_user', JSON.stringify(user));
    } catch { /* storage might be unavailable in some privacy modes */ }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    const f = registerForm;
    // Light client-side guards — backend is the source of truth.
    if (!f.email || !f.password || !f.full_name || !f.date_of_birth || !f.phone
      || !f.address_line1 || !f.city || !f.postcode) {
      toast.error('Please fill in every required field.');
      return;
    }
    if (f.password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const res = await registerUser({
        ...f,
        spend_limit_gbp: f.no_spend_limit_acknowledged ? null : Number(f.spend_limit_gbp),
      });
      persistAuth(res.token, res.user);
      // Refresh in-memory user context on parts of the app that read whoami.
      try { await whoami(); } catch { /* non-fatal */ }
      toast.success('Account created. Now enter your code.');
      setStep(STEP_CODE);
    } catch (err) {
      toast.error(errMsg(err, 'Could not create your account.'));
    } finally {
      setBusy(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      const res = await loginUser(loginForm);
      persistAuth(res.token, res.user);
      try { await whoami(); } catch { /* non-fatal */ }
      toast.success('Signed in. Now enter your code.');
      setStep(STEP_CODE);
    } catch (err) {
      toast.error(errMsg(err, 'Could not sign in.'));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setCodeError('Please enter a code.');
      return;
    }
    setBusy(true);
    setCodeError('');
    try {
      const res = await redeemPartnerCode(trimmed);
      setResult(res);
      setStep(STEP_SUCCESS);
    } catch (err) {
      // Server returns friendly strings for every failure path — surface
      // them inline (no toast, so the user can re-read on a flaky mobile).
      setCodeError(errMsg(err, 'That code isn\u2019t valid or has expired.'));
    } finally {
      setBusy(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={OVERLAY_INITIAL}
          animate={OVERLAY_ANIMATE}
          exit={OVERLAY_EXIT}
          transition={{ duration: 0.2 }}
          className="pd-overlay"
          onClick={() => { if (!busy) onClose?.(); }}
          data-testid="partner-code-modal"
        >
          <motion.div
            initial={PANEL_INITIAL}
            animate={PANEL_ANIMATE}
            exit={PANEL_EXIT}
            transition={{ duration: 0.25 }}
            className="pd-overlay-panel mx-3 sm:mx-0"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="partner-code-modal-title"
          >
            {/* Header — pinned at top of panel. Always visible regardless of
                how far the body scrolls, so the X button and step indicator
                can never slip behind Safari's URL bar. */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-pd bg-surface shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Gift size={18} className="text-[color:var(--pd-gold)] shrink-0" />
                <div className="min-w-0">
                  <div id="partner-code-modal-title" className="font-display text-lg leading-tight truncate">Got a code?</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">
                    {step === STEP_AUTH ? 'Step 1 of 2 · Account' : step === STEP_CODE ? 'Step 2 of 2 · Redeem' : 'Done'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { if (!busy) onClose?.(); }}
                className="p-2 rounded-lg border border-pd text-muted-sage hover:text-[color:var(--pd-text)] shrink-0"
                aria-label="Close"
                data-testid="partner-code-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="pd-overlay-body px-5 py-5">
              {step === STEP_AUTH ? (
                <>
                  <p className="text-sm text-muted-sage mb-4">
                    First set up a quick account so we can hold your entry safely. Already have one? <button type="button" className="text-[color:var(--pd-accent-yellow)] underline underline-offset-2" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} data-testid="partner-code-toggle-auth">Switch to {authMode === 'login' ? 'sign up' : 'sign in'}</button>.
                  </p>

                  {authMode === 'register' ? (
                    <form onSubmit={submitRegister} className="space-y-3" data-testid="partner-code-register-form">
                      <FormRow label="Full name">
                        <input required value={registerForm.full_name} onChange={(e) => setRegisterForm((f) => ({ ...f, full_name: e.target.value }))} className={inputCls} data-testid="pc-reg-fullname" />
                      </FormRow>
                      <FormRow label="Email">
                        <input required type="email" autoComplete="email" value={registerForm.email} onChange={(e) => setRegisterForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="pc-reg-email" />
                      </FormRow>
                      <FormRow label="Password (min 8)">
                        <input required type="password" minLength={8} autoComplete="new-password" value={registerForm.password} onChange={(e) => setRegisterForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} data-testid="pc-reg-password" />
                      </FormRow>
                      <FormRow label="Date of birth (must be 18+)">
                        <input required type="date" value={registerForm.date_of_birth} onChange={(e) => setRegisterForm((f) => ({ ...f, date_of_birth: e.target.value }))} className={inputCls} data-testid="pc-reg-dob" />
                      </FormRow>
                      <FormRow label="Phone">
                        <input required type="tel" value={registerForm.phone} onChange={(e) => setRegisterForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} data-testid="pc-reg-phone" />
                      </FormRow>
                      <FormRow label="Address line 1">
                        <input required value={registerForm.address_line1} onChange={(e) => setRegisterForm((f) => ({ ...f, address_line1: e.target.value }))} className={inputCls} data-testid="pc-reg-addr1" />
                      </FormRow>
                      <div className="grid grid-cols-2 gap-2">
                        <FormRow label="City">
                          <input required value={registerForm.city} onChange={(e) => setRegisterForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} data-testid="pc-reg-city" />
                        </FormRow>
                        <FormRow label="Postcode">
                          <input required value={registerForm.postcode} onChange={(e) => setRegisterForm((f) => ({ ...f, postcode: e.target.value.toUpperCase() }))} className={inputCls} data-testid="pc-reg-postcode" />
                        </FormRow>
                      </div>
                      <FormRow label="Monthly spend limit (£, up to 250)">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={250}
                            step={5}
                            value={registerForm.no_spend_limit_acknowledged ? '' : registerForm.spend_limit_gbp}
                            disabled={registerForm.no_spend_limit_acknowledged}
                            onChange={(e) => setRegisterForm((f) => ({ ...f, spend_limit_gbp: e.target.value === '' ? '' : Number(e.target.value) }))}
                            className={`${inputCls} ${registerForm.no_spend_limit_acknowledged ? 'opacity-40' : ''}`}
                            data-testid="pc-reg-spend-limit"
                          />
                          <label className="flex items-center gap-1.5 text-xs text-muted-sage whitespace-nowrap">
                            <input type="checkbox" checked={registerForm.no_spend_limit_acknowledged} onChange={(e) => setRegisterForm((f) => ({ ...f, no_spend_limit_acknowledged: e.target.checked }))} className="accent-[color:var(--pd-accent-yellow)]" data-testid="pc-reg-no-limit" />
                            No limit
                          </label>
                        </div>
                      </FormRow>
                      <p className="text-[11px] text-muted-sage">
                        You can change your spend limit anytime from Account. The platform £250/month cap always applies.
                      </p>
                      <button type="submit" disabled={busy} className="w-full mt-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pc-reg-submit">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                        Create account &amp; continue
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={submitLogin} className="space-y-3" data-testid="partner-code-login-form">
                      <FormRow label="Email">
                        <input required type="email" autoComplete="email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="pc-login-email" />
                      </FormRow>
                      <FormRow label="Password">
                        <input required type="password" autoComplete="current-password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} data-testid="pc-login-password" />
                      </FormRow>
                      <button type="submit" disabled={busy} className="w-full mt-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pc-login-submit">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                        Sign in &amp; continue
                      </button>
                    </form>
                  )}
                </>
              ) : null}

              {step === STEP_CODE ? (
                <form onSubmit={submitCode} className="space-y-4" data-testid="partner-code-form">
                  <p className="text-sm text-muted-sage">
                    Enter your partner code to unlock <span className="text-[color:var(--pd-text)]">1 free entry</span> into our current live competition.
                  </p>
                  <div>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => { setCode(e.target.value.toUpperCase()); if (codeError) setCodeError(''); }}
                      placeholder="e.g. OCTOBERDRAW"
                      autoFocus
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      className={`w-full h-14 rounded-xl bg-[color:var(--pd-bg)] border-2 ${codeError ? 'border-[color:var(--pd-danger)]' : 'border-pd focus:border-[color:var(--pd-accent-yellow)]'} px-4 font-mono text-xl tracking-widest text-center text-[color:var(--pd-text)] outline-none`}
                      data-testid="partner-code-input"
                    />
                    {codeError ? (
                      <div className="mt-2 flex items-center gap-2 text-xs text-[color:var(--pd-danger)]" data-testid="partner-code-error">
                        <AlertTriangle size={13} />
                        <span>{codeError}</span>
                      </div>
                    ) : null}
                  </div>
                  <button type="submit" disabled={busy} className="w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="partner-code-submit">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                    Redeem code
                  </button>
                  <p className="text-[11px] text-muted-sage text-center">
                    One code = one free entry per account. Codes are set up by The Padel Draw with each partner.
                  </p>
                </form>
              ) : null}

              {step === STEP_SUCCESS && result ? (
                <div className="space-y-5" data-testid="partner-code-success">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-[color:var(--pd-gold)]/15 border border-[color:var(--pd-gold)]/50 flex items-center justify-center mb-3">
                      <CheckCircle2 size={28} className="text-[color:var(--pd-gold)]" />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">You&rsquo;re in</div>
                    <h3 className="font-display text-2xl mt-1 leading-tight">1 free entry added to<br />{result.competition?.title}.</h3>
                    <p className="mt-3 text-sm text-muted-sage">
                      Ticket <span className="font-mono text-[color:var(--pd-text)]">{result.ticket_number}</span>
                      {result.partnership_label ? <> &middot; via {result.partnership_label}</> : null}.
                    </p>
                    <p className="mt-2 text-xs text-muted-sage">
                      A confirmation email is on its way. You can review this entry in your account at any time.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => onClose?.()}
                    className="w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl font-medium"
                    data-testid="partner-code-done"
                  >
                    Done
                  </button>

                  {/* Optional, low-key upsell — gentle, no urgency. */}
                  <div className="pt-4 border-t border-pd">
                    <div className="flex items-center gap-2 mb-2 text-xs text-muted-sage">
                      <Ticket size={13} />
                      <span>
                        Want to buy more entries? Every 2nd ticket is just &pound;1 &mdash; 2 for {' '}
                        {typeof result.competition?.ticket_price_gbp === 'number'
                          ? `£${(result.competition.ticket_price_gbp + 1).toFixed(2)}`
                          : '£3.99'}.
                      </span>
                    </div>
                    <Link
                      to={result.competition?.slug ? `/competitions/${result.competition.slug}` : '/competitions'}
                      onClick={() => onClose?.()}
                      className="block w-full text-center border border-[color:var(--pd-accent-yellow)]/60 text-[color:var(--pd-accent-yellow)] px-4 py-2.5 rounded-xl text-sm hover:bg-[color:var(--pd-accent-yellow)]/10"
                      data-testid="partner-code-upsell"
                    >
                      Buy more entries
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
};

const inputCls = 'w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none';

const FormRow = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1 block">{label}</span>
    {children}
  </label>
);

export default PartnerCodeModal;
